<div class="flex flex-col gap-4 p-4">
  <!-- Regulatory dashboard with expiring-compliances widget -->
  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-default text-xl font-bold">Regulatory Dashboard</h1>
      <p class="text-secondary text-sm">Compliance, document status, integration health and upcoming deadlines</p>
    </div>
  </div>

  <!-- Filter Bar -->
  <div class="rounded-xl border bg-white px-4 py-1">
    <app-global-filter-panel [config]="filterConfig" (filtersChanged)="onFiltersChanged($event)" />
  </div>

  <!-- Compliance KPIs — scoped to the company (trade license + vendors) -->
  <app-kpi-card-grid [cards]="complianceKpis" title="COMPANY COMPLIANCE OVERVIEW" [columns]="5" />

  <!-- Compliances Expiring Soon (30 days) — risk meter + items + link to tab -->
  <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div class="mb-3 flex items-center justify-between gap-3">
      <div>
        <h3 class="text-default text-sm font-bold">Compliances Expiring Soon</h3>
        <p class="text-secondary text-xs">Items due in the next 30 days across all scopes (company, community, unit, vendor)</p>
      </div>
      <a routerLink="/compliances/expiring" class="text-xs font-medium text-blue-600 hover:underline">View all →</a>
    </div>

    @if (!hasCompliancePermission) {
      <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
        <mat-icon class="!text-base text-red-400">lock</mat-icon>
        Permissions Restricted
      </div>
    } @else if (loadingExpiring) {
      <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
    } @else if (expiringComplianceData) {
      <!-- Risk meter progress bar -->
      <div class="mb-4">
        <div
          class="mb-1 flex items-center justify-between text-xs"
          matTooltip="Percentage of compliance items expiring within 30 days, out of all items that have an expiry date set and a document on file."
          matTooltipPosition="above"
        >
          <span class="text-secondary font-semibold uppercase tracking-wide">Expiry Risk</span>
          <span class="text-xs font-bold" [ngClass]="getRiskTextClass(expiringComplianceData.risk_percent ?? 0)">
            {{ expiringComplianceData.risk_percent ?? 0 }}%
            <span class="text-secondary ml-1 font-normal"> ({{ expiringComplianceData.expiring_count ?? 0 }} of {{ expiringComplianceData.total ?? 0 }}) </span>
          </span>
        </div>
        <div class="h-2 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            class="h-full rounded-full transition-all duration-500"
            [ngClass]="getRiskBarColor(expiringComplianceData.risk_percent ?? 0)"
            [style.width.%]="expiringComplianceData.risk_percent ?? 0"
          ></div>
        </div>
      </div>

      <!-- 4 bucket counts -->
      <div class="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div
          class="rounded-lg border border-green-100 bg-green-50 p-2 text-center"
          matTooltip="Compliance items with a document on file that are either permanent (no expiry date) or have more than 30 days left before expiry."
          matTooltipPosition="above"
        >
          <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Healthy</div>
          <div class="mt-0.5 text-xl font-bold text-green-700">{{ expiringComplianceData.healthy_count ?? 0 }}</div>
        </div>
        <div class="rounded-lg border border-amber-100 bg-amber-50 p-2 text-center">
          <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Expiring 30d</div>
          <div class="mt-0.5 text-xl font-bold text-amber-700">{{ expiringComplianceData.expiring_count ?? 0 }}</div>
        </div>
        <div class="rounded-lg border border-red-100 bg-red-50 p-2 text-center">
          <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Expired</div>
          <div class="mt-0.5 text-xl font-bold text-red-700">{{ expiringComplianceData.expired_count ?? 0 }}</div>
        </div>
        <div class="rounded-lg border border-gray-100 bg-gray-50 p-2 text-center">
          <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Missing Docs</div>
          <div class="mt-0.5 text-xl font-bold text-gray-700">{{ expiringComplianceData.missing_docs ?? 0 }}</div>
        </div>
      </div>

      <!-- Top expiring items list — work-queue card style -->
      @if (expiringComplianceData.items?.length > 0) {
        <div class="flex flex-col gap-1">
          @for (it of expiringComplianceData.items; track it.id) {
            <div
              class="flex items-center gap-2.5 overflow-hidden rounded-lg border border-l-4 border-gray-100 bg-white py-2 pl-2.5 pr-2.5 shadow-sm"
              [class.border-l-red-500]="it.days_left <= 7"
              [class.border-l-orange-400]="it.days_left > 7 && it.days_left <= 14"
              [class.border-l-amber-400]="it.days_left > 14"
            >
              <!-- Scope icon badge -->
              <div class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-100">
                <mat-icon class="!text-sm !leading-none text-gray-500">{{ getScopeIcon(it.scope) }}</mat-icon>
              </div>
              <!-- Content -->
              <div class="min-w-0 flex-1">
                <p class="line-clamp-1 text-xs font-semibold leading-snug text-gray-800">{{ it.label_name }}</p>
                <div class="mt-0.5 flex flex-wrap items-center gap-x-1 text-[10px] text-gray-400">
                  <span>{{ getScopeLabel(it.scope) }}: {{ it.entity_name }}</span>
                  @if (it.parent_name) {
                    <span class="text-gray-300">·</span>
                    <span>{{ it.parent_name }}</span>
                  }
                  <span class="text-gray-300">·</span>
                  <span>{{ it.expiry | date: 'mediumDate' }}</span>
                </div>
              </div>
              <!-- Days left badge -->
              <span
                class="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                [class.bg-red-100]="it.days_left <= 7"
                [class.text-red-700]="it.days_left <= 7"
                [class.bg-orange-100]="it.days_left > 7 && it.days_left <= 14"
                [class.text-orange-700]="it.days_left > 7 && it.days_left <= 14"
                [class.bg-amber-100]="it.days_left > 14"
                [class.text-amber-700]="it.days_left > 14"
                >{{ it.days_left }}d left</span
              >
            </div>
          }
        </div>
      } @else {
        <div class="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-8 text-center">
          <mat-icon class="!text-3xl text-gray-300">verified_user</mat-icon>
          <span class="text-xs text-gray-400">No compliances expiring in the next 30 days</span>
        </div>
      }
    }
  </div>

  <!-- Document Compliance + Contract Expiry Tracker + Regulatory Authority Status -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
    <!-- Document Compliance -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-center gap-2">
        <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50">
          <mat-icon class="!text-sm text-blue-500">description</mat-icon>
        </div>
        <h3 class="text-default text-sm font-bold">Document Compliance</h3>
      </div>
      @if (!hasCompliancePermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingDocument) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (documentData) {
        <p class="text-secondary mb-3 text-xs">Tenancy contract (Ejari) expiry status across all units</p>
        <div class="flex flex-col gap-2">
          <!-- Expiring soon -->
          <div class="flex items-center gap-3 rounded-lg border border-l-4 border-gray-100 border-l-orange-400 bg-white px-3 py-2.5 shadow-sm">
            <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-orange-50">
              <mat-icon class="!text-sm text-orange-500">schedule</mat-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs font-semibold text-gray-800">Expiring Soon</p>
              <p class="text-[10px] text-gray-400">Within the next 90 days</p>
            </div>
            <span class="rounded-full px-2 py-0.5 text-sm font-bold" [ngClass]="documentData.ejari_expiring_90d ?? 0 | countBadge">{{ documentData.ejari_expiring_90d ?? 0 }}</span>
          </div>
          <!-- Expired -->
          <div class="flex items-center gap-3 rounded-lg border border-l-4 border-gray-100 border-l-red-500 bg-white px-3 py-2.5 shadow-sm">
            <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-50">
              <mat-icon class="!text-sm text-red-500">event_busy</mat-icon>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-xs font-semibold text-gray-800">Expired</p>
              <p class="text-[10px] text-gray-400">Tenancy contract past due</p>
            </div>
            <span class="rounded-full px-2 py-0.5 text-sm font-bold" [ngClass]="documentData.ejari_expired ?? 0 | countBadge">{{ documentData.ejari_expired ?? 0 }}</span>
          </div>
        </div>
      } @else {
        <div class="text-secondary flex flex-col items-center gap-2 py-6">
          <mat-icon>info</mat-icon>
          <span class="text-sm">No data available</span>
        </div>
      }
    </div>

    <!-- Contract Expiry Tracker -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-work-order-expiry-tracker [dashboardType]="dashboardType" [filters]="filters" [requiredPermission]="'rfp_view'" />
    </div>

    <!-- Regulatory Authority Status -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <h3 class="text-secondary mb-3 text-sm font-semibold uppercase tracking-wide">Regulatory Authority Status</h3>
      @if (!hasCompliancePermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingAuthority) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (authorityData?.items?.length > 0) {
        <div class="space-y-2">
          @for (it of authorityData.items; track it.key) {
            <div class="flex items-center gap-3 rounded-lg border p-2.5" [ngClass]="getAuthorityRowClass(it.status)">
              <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" [ngClass]="getAuthorityIconBg(it.status)">
                <mat-icon class="!h-5 !w-5 !text-[18px] !leading-none" [ngClass]="getAuthorityIconColor(it.status)">{{ it.icon }}</mat-icon>
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-default truncate text-sm font-semibold">{{ it.label }}</p>
                <p class="text-secondary truncate text-xs">{{ it.detail }}</p>
              </div>
              <span class="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold" [ngClass]="getAuthorityBadgeClass(it.status)">
                {{ it.value }}
              </span>
            </div>
          }
        </div>
      } @else if (!loadingAuthority) {
        <p class="text-secondary py-4 text-center text-sm">No authority data available</p>
      }
    </div>
  </div>

  <!-- RDC — Rental Dispute Center -->
  <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <!-- Header -->
    <div class="mb-3 flex items-center justify-between">
      <div>
        <h3 class="text-default text-sm font-bold">RDC — Rental Dispute Center</h3>
        <p class="text-secondary text-xs">Active dispute cases, claimed amounts and community breakdown</p>
      </div>
      @if (hasRdcPermission) {
        <a routerLink="/rdc" class="text-xs font-medium text-blue-600 hover:underline">View all →</a>
      }
    </div>

    @if (!hasRdcPermission) {
      <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
        <mat-icon class="!text-base text-red-400">lock</mat-icon>
        Permissions Restricted
      </div>
    } @else if (loadingRdc) {
      <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
    } @else if (rdcData) {
      <!-- KPI tiles -->
      <div class="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div class="rounded-lg border border-blue-100 bg-blue-50 p-3 text-center">
          <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Active Cases</div>
          <div class="mt-0.5 text-2xl font-bold text-blue-700">{{ rdcData.active ?? 0 }}</div>
        </div>
        <div class="rounded-lg border border-green-100 bg-green-50 p-3 text-center">
          <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Closed Cases</div>
          <div class="mt-0.5 text-2xl font-bold text-green-700">{{ rdcData.closed ?? 0 }}</div>
        </div>
        <div class="rounded-lg border border-gray-100 bg-gray-50 p-3 text-center">
          <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Total Cases</div>
          <div class="mt-0.5 text-2xl font-bold text-gray-700">{{ rdcData.total ?? 0 }}</div>
        </div>
        <div class="rounded-lg border border-orange-100 bg-orange-50 p-3 text-center">
          <div class="text-secondary text-[10px] font-semibold uppercase tracking-wide">Active Claimed</div>
          <div class="mt-0.5 truncate text-lg font-bold text-orange-700" [innerHTML]="rdcData.active_claimed | amount"></div>
        </div>
      </div>

      <!-- Two-column: By Community + Recent Cases -->
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <!-- By Community -->
        <div>
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">By Community (<span class="currency normal-case">&#xe001;</span>)</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          @if (rdcData.by_community?.length > 0) {
            <div class="divide-y divide-gray-50">
              @for (c of rdcData.by_community; track $index) {
                <div class="flex items-center gap-2.5 py-2 transition-colors hover:bg-gray-50">
                  <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                    <mat-icon class="!text-sm text-blue-500">apartment</mat-icon>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-xs font-semibold text-gray-800">{{ c.community_name ?? 'Unknown' }}</div>
                    <div class="text-[10px] text-gray-400">{{ c.active_count }} active · {{ c.count }} total</div>
                  </div>
                  <div class="shrink-0 text-right">
                    <div class="text-xs font-bold text-orange-700">{{ c.total_claimed | number: '1.2-2' }}</div>
                  </div>
                </div>
              }
            </div>
          } @else {
            <p class="text-secondary py-3 text-center text-xs">No community data</p>
          }
        </div>

        <!-- Recent Active Cases -->
        <div>
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Recent Active Cases</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          @if (rdcData.recent_cases?.length > 0) {
            <div class="divide-y divide-gray-50">
              @for (rc of rdcData.recent_cases; track rc.id) {
                <a [routerLink]="['/rdc']" [queryParams]="{ caseId: rc.id }" class="flex cursor-pointer items-start gap-2.5 py-2 text-inherit no-underline transition-colors hover:bg-gray-50">
                  <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-50">
                    <mat-icon class="!text-sm text-red-500">gavel</mat-icon>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-xs font-semibold text-gray-800">{{ rc.case_number }}</div>
                    <div class="text-[10px] text-gray-400">{{ rc.community_name ?? '—' }}{{ rc.unit_no ? ' · Unit ' + rc.unit_no : '' }}</div>
                    <div class="mt-0.5 flex items-center gap-1">
                      <span class="rounded-full px-1.5 py-0.5 text-[9px] font-semibold" [ngClass]="getRdcStatusColor(rc.status)">{{ rc.status }}</span>
                      <span class="text-[10px] text-gray-400">{{ rc.case_type }}</span>
                    </div>
                  </div>
                  <div class="shrink-0 text-right">
                    <div class="text-xs font-bold text-orange-700" [innerHTML]="rc.claimed_amount | amount"></div>
                    <div class="text-[10px] text-gray-400">{{ rc.submission_date | date: 'mediumDate' }}</div>
                  </div>
                </a>
              }
            </div>
          } @else {
            <p class="text-secondary py-3 text-center text-xs">No active cases</p>
          }
        </div>
      </div>
    }
  </div>

  <!-- Bottom Row: Work Queue + Timeline -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-work-queue-list [filters]="filters" title="My Work Queue" />
    </div>
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-activity-timeline [requiredPermission]="'activity_logs_other'" />
    </div>
  </div>
</div>
